import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import facilityService from "../../services/facility";
import queryDefault from "../../utils/queryDefault";
import iconService from "../../services/icon";
import { get } from "lodash";
import { useState } from "react";

export default function FacilityCreate() {
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const schema = yup.object().shape({
    name: yup
      .string()
      .min(2)
      .required("name is a required field and length > 2"),
  });
  const form = useForm({
    defaultValues: {
      name: "",
      admin_name: user.username,
      type: "",
      logo: "",
    },
    resolver: yupResolver(schema),
  });

  const [filter, setFilter] = useState("");

  const getLogoList = useQuery(
    ["icons"],
    () => {
      return iconService.getAllIcon();
    },
    {
      ...queryDefault,
    }
  );

  const createFacility = useMutation(
    "create_facility",
    facilityService.createFacility,
    {
      onSuccess: () => {
        navigate("/app/properties/facility");

        notification.onOpen({
          message: "Facility was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    createFacility.mutate(values);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Facility"
        breadcrumbs={[
          { label: "Facility", href: "/app/properties/facility" },
          { label: "Create Facility", href: "/app/properties/facility/create" },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="facility"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Logo</FormLabel>
                <Controller
                  name="logo"
                  control={form.control}
                  render={({ field }) => (
                    <Autocomplete
                      options={get(getLogoList, "data.data", [])}
                      getOptionLabel={(option: any) => option.name}
                      onChange={(event, newValue) => {
                        form.setValue("logo", newValue ? newValue.name : "");
                      }}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Icon"
                          variant="outlined"
                          value={filter}
                          onChange={(e: any) => {
                            setFilter(e.target.value);
                            if (params.inputProps.onChange) {
                              params.inputProps.onChange(e); // Ensure the input value is managed correctly
                            }
                          }}
                        />
                      )}
                      noOptionsText="No icons available"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value="Property" key="Property">
                          Property
                        </MenuItem>
                        <MenuItem value="Room" key="Room">
                          Room
                        </MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createFacility.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
